import { useState } from 'react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import { Combobox } from '@headlessui/react'
import { useEffect } from 'react';
import { apiUrl } from './config';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

export default function CustomerComboBox(props) {
  const [query, setQuery] = useState('')
  const [selectedPerson, setSelectedPerson] = useState(props.value || null)
  const editable = props.editable;
  const [ loadedCustomer, setLoadedCustomer ] = useState(null);

  // Update the useEffect to better handle external value changes
  useEffect(() => {
    console.log('CustomerComboBox value prop changed:', props.value);
    console.log('Current selectedPerson:', selectedPerson);
    
    // if (props.value !== selectedPerson) {
      setSelectedPerson(props.value);
      
      // Always load customer address when value is set externally, even if it already has address data
      if (props.value && (props.value._id || props.value.id)) {
        console.log('Loading address for externally set customer:', props.value);
        // Use a slight delay to ensure state is updated
        setTimeout(() => {
          LoadCustomerAddress(props.value);
        }, 100);
      } else {
        console.log('Clearing loaded customer - no value or no ID');
        setLoadedCustomer(null);
      }
    // }
  },[props.value, selectedPerson]); // Remove selectedPerson from dependencies to avoid circular updates

  // Update LoadCustomerAddress to handle both _id and id
  function LoadCustomerAddress(selectedPerson) {
    console.log('Loading customer address for:', selectedPerson);
    if (selectedPerson === null) {
      console.log('Selected person is null, clearing loaded customer');
      setLoadedCustomer(null);
      return;
    }

    // Get the customer ID - handle both _id and id
    const customerId = selectedPerson._id || selectedPerson.id;
    if (!customerId) {
      console.error('No customer ID found in selectedPerson:', selectedPerson);
      return;
    }

    const fetchCustomer = async () => {
      try {
        console.log('Fetching customer with ID:', customerId);
        const response = await fetch(apiUrl(`/customer/one?customerId=${customerId}`));
        if (response.ok) {
          var localLoadedCustomer = await response.json();
          console.log('Loaded customer details:', localLoadedCustomer);
          setLoadedCustomer(localLoadedCustomer);
          
          // Debug the address fields
          console.log('Address fields:', {
            address_1: localLoadedCustomer.address_1,
            address_2: localLoadedCustomer.address_2,
            city: localLoadedCustomer.city,
            state: localLoadedCustomer.state,
            zip_code: localLoadedCustomer.zip_code,
            telephone: localLoadedCustomer.telephone,
            contact_email: localLoadedCustomer.contact_email
          });
        } else {
          console.error('Failed to load customer details, status:', response.status);
          setLoadedCustomer(null);
        }
      } catch (error) {
        console.error('Error loading customer details:', error);
        setLoadedCustomer(null);
      }
    }

    fetchCustomer();
  }

  // Add a debug useEffect to track loadedCustomer changes
  useEffect(() => {
    console.log('LoadedCustomer changed:', loadedCustomer);
    if (loadedCustomer) {
      console.log('Formatted address:', formatCustomerAddress(loadedCustomer));
    }
  }, [loadedCustomer]);

  const setSelectedCustomer = (person) => {
    console.log('Customer selected in ComboBox:', person);
    setSelectedPerson(person);
    LoadCustomerAddress(person);
    if (props.onChange) {
      props.onChange(person);
    }
  }

  const handleAddCustomer = () => {
    console.log('Add Customer');
    window.location.href = '/customeradd';
  }

  function editableClass(editable) {
    var classstr = '';
    if (!editable) {
      classstr = 'pointer-events-none ';
    }
    return classstr;
  }

  let addButton = <div></div>;

  const add = props.add;
  const people = props.people || []; // Default to empty array if people is null

  if (add) {
    addButton = (
      <div className="mt-2 pl-2 grow-0 table-cell">
        <button type="button"
                onClick={handleAddCustomer}
                className="block rounded-md bg-indigo-600 px-3 py-2 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
        >
            Add
        </button>
      </div>
    );
  }

  // Helper function to safely format address
  const formatCustomerAddress = (customer) => {
    if (!customer) return '';
    
    const address1 = customer.address_1 || '';
    const address2 = customer.address_2 || '';
    const city = customer.city || '';
    const state = customer.state || '';
    const zipCode = customer.zip_code || '';
    const telephone = customer.telephone || '';
    const email = customer.contact_email || '';
    
    // Combine address lines
    const addressLine = (address1 + ' ' + address2).trim();
    const cityStateZip = (city + ' ' + state + ' ' + zipCode).trim();
    
    // Build address array, filtering out empty lines
    const addressParts = [
      addressLine,
      cityStateZip,
      telephone,
      email
    ].filter(part => part.trim() !== '');
    
    return addressParts.join('\n');
  };

  const filteredPeople =
    query === ''
      ? people
      : people.filter((person) => {
          return person.name && person.name.toLowerCase().includes(query.toLowerCase())
        })


  return (
    <div>
      <Combobox as="div" value={selectedPerson} onChange={setSelectedCustomer}>
        <Combobox.Label className="block text-sm mb-2 font-medium leading-6 text-gray-900">Customer</Combobox.Label>
        <div className="relative table-cell w-full">
          <Combobox.Input
            className="w-full truncate rounded-md border-0 bg-white py-1.5 pl-3 pr-14 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            onChange={(event) => setQuery(event.target.value)}
            displayValue={(person) => person?.name || ''}
          />
          <Combobox.Button className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md focus:outline-none">
            <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
          </Combobox.Button>

          {filteredPeople.length > 0 && (
            <Combobox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
              {filteredPeople.map((person) => (
                <Combobox.Option
                  key={person._id || person.id}
                  value={person}
                  className={({ active }) =>
                    classNames(
                      'relative cursor-default select-none py-2 pl-3 pr-9',
                      active ? 'bg-indigo-600 text-white' : 'text-gray-900'
                    )
                  }
                >
                  {({ active, selected }) => (
                    <>
                      <span className={classNames('block truncate', selected && 'font-semibold')}>{person.name}</span>

                      {selected && (
                        <span
                          className={classNames(
                            'absolute inset-y-0 right-0 flex items-center pr-4',
                            active ? 'text-white' : 'text-indigo-600'
                          )}
                        >
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Combobox.Option>
              ))}
            </Combobox.Options>
          )}
        </div>
        {addButton}
      </Combobox>

    {(props.addressBlock !== false) ? (
      <div className="w-full min-w-[22rem] pt-6 sm:mt-0 sm:flex-none">
        <label htmlFor="comment" className={editableClass(editable) + 'block text-sm font-medium leading-6 text-gray-900'}>
          Address
        </label>
        <div className="mt-2">
          <textarea
            rows={4}
            name="comment"
            id="comment"
            className={editableClass(editable) + 'block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6'}
            value={loadedCustomer ? formatCustomerAddress(loadedCustomer) : ''}
            placeholder={loadedCustomer ? '' : 'Customer address will appear here'}
            readOnly={!editable}
            onChange={() => {}} // Add empty onChange to prevent React warning
          />
        </div>
      </div>
    ) : (
      <div></div>
    )}

    </div>
  )
  
}